import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Button from 'react-bootstrap/Button';
import Spinner from 'react-bootstrap/Spinner';
import toast from 'react-hot-toast';
import { Trans, useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

import { useTax1099Api } from '../../resources/hooks/api/useTax1099ApiHook';
import { useAllVendors } from '../../resources/useAllVendors';
import { usePayerData } from '../../resources/hooks/usePayerDataHook';
import { URLS } from '../../resources/constants';
import { getBranch } from '../../store/user/selectors';

interface SyncItem {
  id: string | number;
  date: string;
  narrative: string;
  amount: number;
  paymentType: string;
  typeBadge: 'blue' | 'gray';
  matchedVendor: string;
  isAutoLinked: boolean;
  isExempt: boolean;
  unmappedId?: string;
}

// Values are translation keys under `payment_methods.*`.
const PAYMENT_METHOD_MAP: Record<number, string> = {
  1: 'check',
  2: 'ach_direct',
  3: 'wire_transfer',
  4: 'cash',
  5: 'direct_deposit',
  10: 'credit_card',
  11: 'debit_card',
  12: 'third_party_network',
  99: 'other_non_reportable',
};

const Tax1099ExpenseSync: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation('Tax1099ExpenseSync');
  const { branchId, businessName } = usePayerData();

  const [syncStatus, setSyncStatus] = useState<'idle' | 'syncing' | 'synced' | 'error'>('idle');
  const hasAutoSynced = useRef(false);
  const [loading, setLoading] = useState(false);
  const [payments, setPayments] = useState<any[]>([]);
  // Was a single 200-row page, so on a branch with more vendors than that the
  // rest were silently missing. Shared hook pages through all of them.
  const { vendors } = useAllVendors({ branchId });
  const [selectedPayeeMap, setSelectedPayeeMap] = useState<Record<string, number>>({});
  const [resolvedIds, setResolvedIds] = useState<Record<string, string>>({});

  const { syncExpenses, fetchPaymentList, updatePayment } = useTax1099Api();

  const loadData = useCallback(() => {
    if (!branchId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    fetchPaymentList(
      { branch_id: branchId, page: 1, page_size: 50 },
      (_msg: string, response: any) => {
        setLoading(false);
        const list = response?.data?.data?.results ?? [];
        setPayments(list);
      },
      () => {
        setLoading(false);
        setPayments([]);
      }
    );
    // eslint-disable-next-line
  }, [branchId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Automatically sync RevRex expenses in the background on initial wizard open
  useEffect(() => {
    if (!branchId || hasAutoSynced.current) {
      return;
    }
    hasAutoSynced.current = true;
    setSyncStatus('syncing');
    syncExpenses(
      { branch_id: branchId },
      () => {
        setSyncStatus('synced');
        loadData();
      },
      () => {
        setSyncStatus('error');
      }
    );
  }, [branchId, syncExpenses, loadData]);

  const handleResolveMapping = (unmappedKey: string) => {
    const targetVendorId = selectedPayeeMap[unmappedKey];
    const vendorObj = vendors.find((v) => v.id === targetVendorId);
    const vendorName = vendorObj?.display_name || t('vendor.selected_fallback');

    if (!targetVendorId) {
      toast.error(t('toasts.choose_vendor_first'));
      return;
    }

    updatePayment(
      { id: Number(unmappedKey), vendor_id: targetVendorId, branch_id: branchId },
      () => {
        setResolvedIds((prev) => ({ ...prev, [unmappedKey]: vendorName }));
        toast.success(t('toasts.match_success', { vendor: vendorName }));
        loadData();
      },
      (msg: string) => toast.error(msg || t('toasts.update_failed'))
    );
  };

  const items: SyncItem[] = useMemo(() => {
    return payments.map((p: any) => {
      const methodVal = Number(p.payment_method);
      const isCard = methodVal === 10 || methodVal === 11 || methodVal === 12;
      const isExempt = !p.is_1099_reportable || isCard;
      const matchedVendorName =
        p.vendor_display_name || p.vendor_name || (p.vendor_id ? t('vendor.fallback', { id: p.vendor_id }) : '');
      const methodLabel = PAYMENT_METHOD_MAP[methodVal]
        ? t(`payment_methods.${PAYMENT_METHOD_MAP[methodVal]}`)
        : typeof p.payment_method === 'string'
        ? p.payment_method
        : t('payment_methods.disbursement');

      return {
        id: p.id,
        date: p.payment_date ? String(p.payment_date) : '—',
        narrative: p.reference_number
          ? t('narrative.ref', { ref: p.reference_number })
          : p.memo || p.description || t('narrative.disbursement', { id: p.id }),
        amount: Number(p.gross_amount || p.amount || 0),
        paymentType: isCard
          ? t('payment_type.excluded', { method: methodLabel })
          : t('payment_type.reportable', { method: methodLabel }),
        typeBadge: isCard ? 'gray' : 'blue',
        matchedVendor: isExempt
          ? t('vendor.excluded')
          : resolvedIds[p.id] || matchedVendorName || t('vendor.unmapped'),
        isAutoLinked: Boolean(p.vendor_id || resolvedIds[p.id]),
        isExempt,
        unmappedId: !p.vendor_id && !isExempt ? String(p.id) : undefined,
      };
    });
  }, [payments, resolvedIds, t]);

  const pendingCount = items.filter((i) => i.unmappedId && !resolvedIds[i.unmappedId]).length;

  return (
    <div className="py-2">
      {/* Top Header Navigation */}
      <div className="d-flex justify-content-between align-items-center mb-3 border-bottom pb-2">
        <Button
          variant="outline-primary"
          size="sm"
          style={{
            borderColor: '#14509e',
            color: '#14509e',
            backgroundColor: '#fff',
            fontWeight: 700,
          }}
          onClick={() => navigate(URLS.TAX_1099_PAYMENTS)}
        >
          {t('nav.payment_ledger')}
        </Button>
      </div>

      {/* VIEW 3: AUTOMATED EXPENSE SYNC & IMPORT WIZARD */}
          <div className="d-flex flex-wrap justify-content-between align-items-end mb-4 gap-3">
            <div>
              <p
                style={{
                  fontSize: '11px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.08em',
                  fontWeight: 800,
                  color: '#1687c8',
                  marginBottom: '4px',
                }}
              >
                {t('header.eyebrow')}
              </p>
              <h1 className="h3 font-weight-bold text-dark mb-1" style={{ color: '#0d2c55', fontWeight: 900 }}>
                {t('header.title')}
              </h1>
              <p className="text-muted small mb-0">
                <Trans t={t} i18nKey="header.description" components={{ 1: <code /> }} />
              </p>
            </div>
            <div className="d-flex align-items-center gap-2">
              {syncStatus === 'syncing' && (
                <div
                  className="d-flex align-items-center gap-2 px-3 py-2 rounded-pill border"
                  style={{
                    backgroundColor: '#f0f9ff',
                    borderColor: '#bae6fd',
                    fontSize: '12px',
                    fontWeight: 600,
                    color: '#0369a1',
                  }}
                >
                  <Spinner animation="border" size="sm" style={{ width: '13px', height: '13px' }} />
                  <span>{t('sync_status.syncing')}</span>
                </div>
              )}

              {syncStatus === 'synced' && (
                <div
                  className="d-flex align-items-center gap-2 px-3 py-2 rounded-pill border"
                  style={{
                    backgroundColor: '#f0fdf4',
                    borderColor: '#bbf7d0',
                    fontSize: '12px',
                    fontWeight: 600,
                    color: '#15803d',
                  }}
                  title={t('sync_status.synced_tooltip')}
                >
                  <span
                    style={{
                      width: '8px',
                      height: '8px',
                      borderRadius: '50%',
                      backgroundColor: '#22c55e',
                      display: 'inline-block',
                    }}
                  />
                  <span>{t('sync_status.synced')}</span>
                </div>
              )}

              {syncStatus === 'error' && (
                <div
                  className="d-flex align-items-center gap-2 px-3 py-2 rounded-pill border"
                  style={{
                    backgroundColor: '#fffbeb',
                    borderColor: '#fde68a',
                    fontSize: '12px',
                    fontWeight: 600,
                    color: '#b45309',
                  }}
                  title={t('sync_status.error_tooltip')}
                >
                  <span
                    style={{
                      width: '8px',
                      height: '8px',
                      borderRadius: '50%',
                      backgroundColor: '#f59e0b',
                      display: 'inline-block',
                    }}
                  />
                  <span>{t('sync_status.error')}</span>
                </div>
              )}
            </div>
          </div>

          {/* WIZARD STEP CARD */}
          <div
            className="card shadow-sm border p-4 mb-4 bg-white"
            style={{ borderRadius: '16px', border: '1px solid #dfe7f0' }}
          >
            {/* STEPS */}
            <div className="row g-3 pb-4 mb-4 border-bottom">
              <div className="col-12 col-md-4">
                <div className="d-flex align-items-center gap-3 text-primary fw-bold small">
                  <span
                    style={{
                      width: '28px',
                      height: '28px',
                      borderRadius: '50%',
                      backgroundColor: '#14509e',
                      color: '#fff',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: 900,
                      fontSize: '12px',
                    }}
                  >
                    1
                  </span>
                  <span>{t('steps.ingestion')}</span>
                </div>
              </div>
              <div className="col-12 col-md-4">
                <div className="d-flex align-items-center gap-3 text-primary fw-bold small">
                  <span
                    style={{
                      width: '28px',
                      height: '28px',
                      borderRadius: '50%',
                      backgroundColor: '#14509e',
                      color: '#fff',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: 900,
                      fontSize: '12px',
                    }}
                  >
                    2
                  </span>
                  <span>{t('steps.screening')}</span>
                </div>
              </div>
              <div className="col-12 col-md-4">
                <div
                  className="d-flex align-items-center gap-3 fw-bold small"
                  style={{ color: pendingCount > 0 ? '#b45309' : '#14509e' }}
                >
                  <span
                    style={{
                      width: '28px',
                      height: '28px',
                      borderRadius: '50%',
                      backgroundColor: pendingCount > 0 ? '#f59e0b' : '#16a34a',
                      color: '#fff',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: 900,
                      fontSize: '12px',
                    }}
                  >
                    3
                  </span>
                  <span>{t('steps.matching')}</span>
                </div>
              </div>
            </div>

            {/* STEP 3 DETAIL: UNMAPPED RECONCILIATION */}
            {pendingCount > 0 && (
              <div
                className="p-3 rounded-3 mb-4 d-flex justify-content-between align-items-center"
                style={{ backgroundColor: '#fffbeb', border: '1px solid #fde68a' }}
              >
                <div>
                  <strong className="d-block small fw-bold" style={{ color: '#78350f' }}>
                    {t('review.pending_count', { count: pendingCount })}
                  </strong>
                  <p className="mb-0" style={{ fontSize: '11px', color: '#92400e' }}>
                    {t('review.description')}
                  </p>
                </div>
                <span
                  className="badge"
                  style={{
                    backgroundColor: '#fef3c7',
                    color: '#92400e',
                    fontWeight: 700,
                    borderRadius: '999px',
                    padding: '6px 12px',
                    fontSize: '10px',
                  }}
                >
                  {t('review.badge')}
                </span>
              </div>
            )}

            <div className="table-responsive">
              <table className="table table-hover align-middle mb-0">
                <thead>
                  <tr className="text-muted small">
                    <th>{t('table.headers.date')}</th>
                    <th>{t('table.headers.narrative')}</th>
                    <th>{t('table.headers.amount')}</th>
                    <th>{t('table.headers.payment_type')}</th>
                    <th>{t('table.headers.matched_vendor')}</th>
                    <th>{t('table.headers.action')}</th>
                  </tr>
                </thead>
                <tbody>
                  {loading && (
                    <tr>
                      <td colSpan={6} className="text-center py-4 text-muted">
                        <Spinner animation="border" size="sm" className="me-2" />
                        {t('table.loading')}
                      </td>
                    </tr>
                  )}
                  {!loading && items.length === 0 && (
                    <tr>
                      <td colSpan={6} className="text-center py-5 text-muted">
                        <div className="mb-2" style={{ fontSize: '28px' }}>⚡</div>
                        <div className="fw-bold mb-1 text-dark">{t('empty.title')}</div>
                        <div className="small">
                          <Trans
                            t={t}
                            i18nKey="empty.description"
                            values={{ businessName }}
                            components={{ 1: <strong /> }}
                          />
                        </div>
                        <div className="small text-muted mt-1 mb-2">
                          {t('empty.hint')}
                        </div>
                      </td>
                    </tr>
                  )}
                  {!loading &&
                    items.map((row) => {
                      const isUnmapped = row.unmappedId && !resolvedIds[row.unmappedId];
                      return (
                        <tr
                          key={row.id}
                          style={{
                            backgroundColor: isUnmapped
                              ? 'rgba(254, 243, 199, 0.4)'
                              : row.isExempt
                              ? 'rgba(249, 250, 251, 0.6)'
                              : 'rgba(240, 253, 244, 0.4)',
                          }}
                        >
                          <td>{row.date}</td>
                          <td>
                            <code>{row.narrative}</code>
                          </td>
                          <td>
                            <strong className={isUnmapped ? 'text-amber-900' : 'text-dark'}>
                              ${row.amount.toFixed(2)}
                            </strong>
                          </td>
                          <td>
                            <span
                              className="badge"
                              style={{
                                backgroundColor: row.typeBadge === 'blue' ? '#edf6fc' : '#eff2f6',
                                color: row.typeBadge === 'blue' ? '#14509e' : '#6b7280',
                                fontWeight: 700,
                                borderRadius: '999px',
                                padding: '4px 8px',
                                fontSize: '10px',
                              }}
                            >
                              {row.paymentType}
                            </span>
                          </td>
                          <td>
                            {isUnmapped ? (
                              <select
                                className="form-select form-select-sm"
                                style={{ maxWidth: '240px', fontSize: '11px', borderColor: '#fcd34d' }}
                                value={selectedPayeeMap[row.unmappedId!] || ''}
                                onChange={(e) => {
                                  const val = Number(e.target.value);
                                  setSelectedPayeeMap((prev) => ({ ...prev, [row.unmappedId!]: val }));
                                }}
                              >
                                <option value="">{t('vendor.choose_placeholder', { count: vendors.length })}</option>
                                {vendors.map((v) => (
                                  <option key={v.id} value={v.id}>
                                    {v.display_name} {v.vendor_code ? `(${v.vendor_code})` : ''}
                                  </option>
                                ))}
                              </select>
                            ) : row.isExempt ? (
                              <span className="text-muted small">{row.matchedVendor}</span>
                            ) : (
                            <span
                              className="badge"
                              style={{
                                backgroundColor: '#eaf7f1',
                                color: '#137a55',
                                fontWeight: 700,
                                borderRadius: '999px',
                                padding: '4px 8px',
                                fontSize: '10px',
                              }}
                            >
                              ✓ {row.matchedVendor}
                            </span>
                          )}
                        </td>
                        <td>
                          {isUnmapped ? (
                            <Button
                              variant="primary"
                              size="sm"
                              className="py-1 px-2 fw-bold"
                              style={{ backgroundColor: '#14509e', borderColor: '#14509e', fontSize: '11px' }}
                              onClick={() => handleResolveMapping(row.unmappedId!)}
                            >
                              {t('actions.confirm_match')}
                            </Button>
                          ) : row.isExempt ? (
                            <span className="small text-muted">{t('status.exempt')}</span>
                          ) : (
                            <span className="small text-success fw-bold">{t('status.auto_linked')}</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
    </div>
  );
};

export default Tax1099ExpenseSync;
